import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { DEFAULT_SERVER_BASE, getServerBase, setServerBase } from "../lib/settings";
import { pingHealthAt } from "../lib/api";

export function Settings() {
  const nav = useNavigate();
  const [value, setValue] = useState(() => getServerBase());
  const [status, setStatus] = useState<"idle" | "testing" | "ok" | "fail">("idle");
  const [message, setMessage] = useState<string>("");
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const test = async () => {
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setStatus("testing");
    setMessage("");
    const ok = await pingHealthAt(value, ctl.signal);
    if (ctl.signal.aborted) return;
    if (ok) {
      setStatus("ok");
      setMessage("Reachable.");
    } else {
      setStatus("fail");
      setMessage("Could not reach server. Is MobAI running and the HTTP API enabled?");
    }
  };

  const save = () => {
    setServerBase(value);
    nav("/", { replace: true });
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-8">
      <h1 className="text-lg font-semibold mb-1">Server configuration</h1>
      <p className="text-sm text-surface-400 mb-6">
        MobAI Web talks to the MobAI desktop app over HTTP. Enter the URL of
        your local MobAI server (default <code className="text-surface-200">{DEFAULT_SERVER_BASE}</code>).
      </p>

      <label className="block text-xs uppercase tracking-wide text-surface-500 mb-1">
        Server URL
      </label>
      <div className="flex items-center gap-2">
        <input
          className="flex-1 bg-surface-800 border border-surface-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent-cyan"
          value={value}
          placeholder={DEFAULT_SERVER_BASE}
          onChange={(e) => {
            setValue(e.target.value);
            setStatus("idle");
          }}
        />
        <button
          type="button"
          onClick={test}
          disabled={status === "testing"}
          className="px-3 py-2 rounded-md border border-surface-600 text-sm hover:bg-surface-800 disabled:opacity-50"
        >
          {status === "testing" ? "Testing…" : "Test"}
        </button>
      </div>
      {status === "ok" && (
        <div className="mt-2 flex items-center gap-1.5 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" />
          {message}
        </div>
      )}
      {status === "fail" && (
        <div className="mt-2 flex items-center gap-1.5 text-sm text-rose-400">
          <XCircle className="w-4 h-4" />
          {message}
        </div>
      )}

      <div className="mt-6 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!value}
          className="px-4 py-2 rounded-md bg-accent-cyan/20 text-accent-cyan border border-accent-cyan/40 text-sm hover:bg-accent-cyan/30 disabled:opacity-50"
        >
          Save
        </button>
        <Link to="/" className="px-4 py-2 rounded-md border border-surface-600 text-sm text-surface-300 hover:bg-surface-800">
          Cancel
        </Link>
      </div>

    </div>
  );
}
